import { superficie, texto } from '@anery/design-system';

import { normalizarCid } from './lerEml';
import { CLASSE_DO_TRECHO } from './marcarTrecho';

/**
 * `allow-same-origin` fica para a tela poder marcar o trecho dentro do quadro. Ele SÓ é seguro porque não há
 * `allow-scripts`: com os dois juntos, o HTML do remetente roda na origem do app e pode tirar o próprio
 * `sandbox` do quadro. Nunca acrescentar `allow-scripts` aqui.
 */
export const SANDBOX_DO_CORPO = 'allow-same-origin';

/** Só imagem `data:` carrega: nada é buscado fora (pixel de rastreio, CSS ou fonte remota). */
export const CSP_DO_CORPO = "default-src 'none'; img-src data:; style-src 'unsafe-inline'; font-src data:";

// O corpo em HTML mora num `iframe`, fora do tema do MUI: as cores entram na folha de estilo pelo valor do token.
const ESTILO_DO_TRECHO = `background:${superficie.realce};color:inherit;border-radius:2px;box-shadow:0 0 0 2px ${superficie.realce}`;

/**
 * Tira toda `<meta>` do HTML do remetente. A CSP não governa a navegação do próprio quadro, e um
 * `<meta http-equiv="refresh">` poderia trocar o e-mail por uma página remota; uma segunda CSP do remetente
 * também não tem o que fazer aqui. O HTML é lido pelo `DOMParser` (que não roda script nem busca nada) em vez de
 * regex, para atributo com `>` entre aspas não deixar a tag pela metade.
 */
function semMeta(html: string): string {
  const lido = new DOMParser().parseFromString(html, 'text/html');
  for (const meta of Array.from(lido.querySelectorAll('meta'))) meta.remove();
  return lido.documentElement.outerHTML;
}

/**
 * O HTML do e-mail vai num `iframe` sem script (`sandbox` = {@link SANDBOX_DO_CORPO}) e com CSP
 * ({@link CSP_DO_CORPO}) declarada antes de qualquer conteúdo do remetente: nada do remetente roda, e imagem
 * remota não é buscada.
 */
export function documentoDoCorpo(html: string, imagensPorCid: ReadonlyMap<string, string>): string {
  const comImagens = semMeta(html).replace(/(["'(])cid:([^"')\s]+)/gi, (trecho, abertura: string, cid: string) => {
    const url = imagensPorCid.get(normalizarCid(cid));
    return url === undefined ? trecho : `${abertura}${url}`;
  });
  return [
    '<!doctype html><html><head><meta charset="utf-8">',
    `<meta http-equiv="Content-Security-Policy" content="${CSP_DO_CORPO}">`,
    '<base target="_blank">',
    '<style>',
    `body{margin:12px;font-family:system-ui,-apple-system,"Segoe UI",sans-serif;font-size:14px;color:${texto.primario};background:${superficie.cartao};word-break:break-word}`,
    'img{max-width:100%;height:auto}',
    // HTML do Outlook põe a logo em `position:absolute` e desenha outra versão (VML) só para ele; no navegador
    // ela flutua por cima do texto. Volta para o fluxo, como o Gmail faz.
    '[style*="position:absolute"],[style*="position: absolute"],[style*="position:fixed"]{position:static!important}',
    `mark.${CLASSE_DO_TRECHO}{${ESTILO_DO_TRECHO}}`,
    '</style></head><body>',
    comImagens,
    '</body></html>',
  ].join('');
}
